type KeysOfUnion<T> = T extends T ? keyof T : never;

export function deepCopy<T>(obj: T): T {
  if (!obj) {
    return obj;
  }

  return JSON.parse(JSON.stringify(obj));
}

export function conditionalAssign<T extends {} | undefined | null>(target: T, source: Partial<T>): void {
  if (!target) {
    return;
  }

  Object.assign(target, source);
}

export function assignSafe<T extends {}>(target: T, source: Partial<T>): T {
  Object.assign(target, source);

  return target;
}

export function extendSafe<TResult extends TBase, TBase extends {}>(base: TBase, source: Omit<TResult, KeysOfUnion<TBase>>): TResult {
  return <TResult>Object.assign({}, base, source);
}

export function stripUndefinedProperties<T>(obj: any): T {
  return deepCopy(obj);
}

export function mergeDeep<T>(target: any, source: any): T {
  const output: any = Object.assign({}, target);

  if (isObject(target) && isObject(source)) {
    Object.entries(source).forEach(([key, value]) => {
      if (isObject(value)) {
        if (!(key in target)) {
          Object.assign(output, { [key]: value });
        } else {
          output[key] = mergeDeep(target[key], value);
        }
      } else {
        Object.assign(output, { [key]: value });
      }
    });
  }

  return output;
}

export function mergeDeepDefined<T>(target: any, source: any): T {
  const undefinedRemoved = stripUndefinedProperties(source);

  return mergeDeep(target, undefinedRemoved);
}

export function isObject(obj: any): boolean {
  return obj && typeof obj === 'object' && !Array.isArray(obj);
}

export function constructObject<T extends object>(type: new () => T, changes: Partial<T>): T {
  return <T>Object.assign(new type(), changes);
}

export function tryOrUndefined<T>(func: () => T): T | undefined {
  try {
    return func();
  } catch {
    return undefined;
  }
}